import PropTypes from 'prop-types';
import React from 'react';
import { BodyLink } from 'js/design_system/link/BodyLink';

const formatDelta = (delta) => {
  if (delta > 0) return `+${delta}`;
  if (delta < 0) return `${delta}`;
  return '—';
};

const deltaClass = (delta) => (delta > 0 ? 'u-tcGreen' : 'u-tcGrayDark');

const Stat = ({ value, label }) => (
  <div className="col-xs-6 col-sm-3 u-taCenter u-pv3x">
    <div className="u-fs36 u-fw700 u-tcGrayDarkest">
      {value.toLocaleString()}
    </div>
    <div className="u-fs12 u-tcGrayDark">{label}</div>
  </div>
);

Stat.propTypes = {
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
};

// The stat tiles count every paper and country; the tables only carry the top
// few. Without this an author counts the rows and finds they don't add up.
const MoreRow = ({ shown, total, label, plural, columns }) => {
  const remainder = total - shown;
  if (remainder <= 0) return null;

  const noun = remainder === 1 ? label : plural || `${label}s`;
  return (
    <tr>
      <td className="u-tcGrayDark" colSpan={columns}>
        and {remainder.toLocaleString()} more {noun}
      </td>
    </tr>
  );
};

MoreRow.propTypes = {
  shown: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  plural: PropTypes.string,
  columns: PropTypes.number.isRequired,
};

// Exclusive: the share of that week's authors who were recommended strictly
// fewer times than this one, so the complement is the band to print --
// everyone at or above the author, their own tie group included. Clamped at 1
// because a lone author at the top of a 6M-row week rounds to a percentile of
// 1 and "top 0%" is not a thing.
const topPercent = (percentile) =>
  Math.max(Math.round((1 - percentile) * 100), 1);

// Shown only when at least half the week did strictly worse. Below that the
// rank is true and worth less than silence, and a week that was never ranked
// has no percentile at all. The reach email that leads on this figure
// suppresses it on the same test — the two have to agree, because an email
// claiming "top 12%" that lands on a page with no rank on it reads as a
// made-up number.
const showsRank = (percentile) =>
  typeof percentile === 'number' && percentile >= 0.5;

const weekLabel = (weekStart) => {
  if (!weekStart) return 'this week';
  const date = new Date(`${weekStart}T00:00:00`);
  return `week of ${date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  })}`;
};

export const ReachSummary = ({ reach }) => (
  <div className="a-fadeInDown">
    <div className="panel panel-flat">
      <div className="panel-body">
        <div className="u-taCenter u-fs18 u-tcGrayDarkest u-mb3x">
          Academia featured your work in{' '}
          <strong>{reach.recommendations.toLocaleString()}</strong>{' '}
          recommendation
          {reach.recommendations === 1 ? '' : 's'} sent to readers —{' '}
          {weekLabel(reach.week_start)}.
        </div>
        <div className="row">
          <Stat value={reach.recommendations} label="Recommendations" />
          <Stat value={reach.distinct_readers} label="Readers" />
          <Stat value={reach.distinct_countries} label="Countries" />
          <Stat value={reach.distinct_works} label="Papers" />
        </div>
        {showsRank(reach.percentile) && (
          <div className="u-taCenter u-fs14 u-tcGrayDarkest u-pt3x">
            That puts you in the{' '}
            <strong>top {topPercent(reach.percentile)}%</strong> of the authors
            whose work we recommended last week.
          </div>
        )}
        {reach.recent_recommendations > 0 && (
          <div className="u-taCenter u-fs12 u-tcGrayDark u-pt3x">
            {reach.recent_recommendations.toLocaleString()} recommendations in
            the last {reach.recent_days} days.
          </div>
        )}
      </div>
    </div>

    {reach.works.length > 0 && (
      <div className="panel panel-flat">
        <div className="panel-body" style={{ overflowX: 'auto' }}>
          <table className="analytics-dashboard-data-table table table-bordered table-hover u-mb0x">
            <thead>
              <tr>
                <th className="col-xs-8">
                  {reach.distinct_works > reach.works.length
                    ? 'Top papers'
                    : 'Paper'}
                </th>
                <th className="col-xs-2">Recommendations</th>
                <th className="col-xs-2">vs last week</th>
              </tr>
            </thead>
            <tbody>
              {reach.works.map((work) => (
                <tr key={work.id}>
                  <td>
                    <BodyLink href={work.url}>{work.title}</BodyLink>
                  </td>
                  <td>{work.count.toLocaleString()}</td>
                  <td className={deltaClass(work.delta)}>
                    {formatDelta(work.delta)}
                  </td>
                </tr>
              ))}
              <MoreRow
                shown={reach.works.length}
                total={reach.distinct_works}
                label="paper"
                columns={3}
              />
            </tbody>
          </table>
        </div>
      </div>
    )}

    {reach.countries.length > 0 && (
      <div className="panel panel-flat">
        <div className="panel-body" style={{ overflowX: 'auto' }}>
          <table className="analytics-dashboard-data-table table table-bordered table-hover u-mb0x">
            <thead>
              <tr>
                <th className="col-xs-8">Where your readers are</th>
                <th className="col-xs-4">Readers</th>
              </tr>
            </thead>
            <tbody>
              {reach.countries.map((country) => (
                <tr key={country.code}>
                  <td>{country.name}</td>
                  <td>{country.count.toLocaleString()}</td>
                </tr>
              ))}
              <MoreRow
                shown={reach.countries.length}
                total={reach.distinct_countries}
                label="country"
                plural="countries"
                columns={2}
              />
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
);

ReachSummary.propTypes = {
  reach: PropTypes.shape({
    week_start: PropTypes.string,
    recommendations: PropTypes.number.isRequired,
    distinct_readers: PropTypes.number.isRequired,
    distinct_countries: PropTypes.number.isRequired,
    distinct_works: PropTypes.number.isRequired,
    recent_recommendations: PropTypes.number,
    recent_days: PropTypes.number,
    percentile: PropTypes.number,
    works: PropTypes.array.isRequired,
    countries: PropTypes.array.isRequired,
  }).isRequired,
};
